/**
 * Deliverable copy — from "355x · Banana, Fresh (150 CT or approx. 7\") · Bulk
 * Tray → 1/2 Banana per serving" to what the kitchen packs, as tokens a chef
 * can scan: `355` `fresh banana halves`, packed in `Bulk Tray(s)`.
 *
 * Ordo stores SERVINGS and a per-serving PORTION under a catalog name. A chef
 * reads a row for three facts, in this order: what do I make, how much, how do
 * I pack it. This module answers them per row (Hunter, Sep 18 2026; Abner's
 * deliverables brief and his Sep 22 feedback):
 *
 *   contents    one line per food (then condiments, then supplies), each a
 *               number token (`qty`) and the words after it (`label`)
 *   pack        the chip that says how it ships: "Bulk Tray(s)", "24 Containers
 *               with Clear Lid (24 oz)", "Jugs"
 *   eachLine    for containers only, what goes in each one: "4 pancakes"
 *
 * Three shapes decide the numbers:
 *   containers  a non-bulk INDIVIDUAL packaging SKU: `qty` IS the container
 *               count; each content line carries the per-container amount and
 *               the whole-order `total`.
 *   bulk        a BULK-kind SKU (Bulk Tray, Bulk Delivery Box) or a bulk-flagged
 *               line: Ordo does not know how many vessels, so contents are
 *               TOTALS (servings x portion) and the pack chip has no count.
 *   loose       no packaging line at all (milk jugs, cartons, custom slices):
 *               the total is the whole story.
 *
 * Names go through `kitchenName` ("fresh bananas", "packets of ketchup",
 * "lactose free 1% lowfat milk"); the catalog string stays on the detail panel.
 * The bulk/containers split keys on the SKU KIND, not the line's bulk flag
 * alone: 324 lines carry a Bulk Delivery Box with `isBulk` false.
 *
 * Pure. Unit-tested against the real rows in the deliverable dictionary
 * (`docs/abner-workspace/chefs-redesign/index.html#/deliverable-dictionary`).
 */
import { formatFractionalValue, formatMeasureText, toDisplaySystem } from '@ordo/constants/measure-format'
import { measureDimension, volumeToCups } from '@ordo/constants/measures'

import { CHEF_SERVICE_MATERIALS_VISIBLE } from '../../../../shared/chef-packaging-visibility'
import { type KitchenName, kitchenName, pluralizeFood, sentenceCase, titleCaseFood } from './kitchen-name'
import { isGenericCountUnit, optionalPlural, pluralizeNoun, pluralizePackagingName, pluralizeWord } from './plural'
import type { Component, Deliverable, SupplyLine } from './types'

export type DeliverableMode = 'containers' | 'bulk' | 'loose'

/** Which picture the pack chip wears. Derived from the packaging name or the vessel word. */
export type PackIcon = 'tray' | 'box' | 'cup' | 'bag' | 'jug' | 'plate' | 'container' | 'none'

export interface ContentTokens {
  /** The number that leads the item: "355", "11.1 gal", "1 cup", "46". Empty when nothing is known. */
  qty: string
  /** The words after it: "fresh bananas", "shredded lettuce", "packets of ketchup", "fresh banana halves". */
  label: string
}

export interface ContentLine extends ContentTokens {
  kind: 'food' | 'condiment' | 'supply'
  /**
   * The food inside `label`, as written there ("fresh bananas" in "fresh
   * bananas", "ketchup" in "packets of ketchup", "fresh banana" in "fresh banana
   * halves"). The item chip bolds it and leaves the rest of the label regular.
   */
  food: string
  /** Set on a measured line ("11.1 gal" + "shredded lettuce"); the chip then reads "11.1 gal of Shredded Lettuce". */
  measured?: true
  /** The food's catalog photo, when the wire carried one. */
  imageUrl?: string
  /** Total volume in US cups, for volume-portioned bulk and loose lines; the pan catalog divides it into pans. */
  volumeCups?: number
  /** `qty` and `label` as one string: "355 fresh bananas", "11.1 gal shredded lettuce". */
  phrase: string
  /** The Cards amount cell: like `qty`, but a vessel count reads "46 packets" there. */
  amount: string
  /** The Cards name cell, sentence case: "Fresh bananas", "Ketchup". */
  name: string
  /** Containers only: the whole-order total for this item ("96" + "pancakes", "4 cups" + "cubed fresh melon"). */
  total?: ContentTokens
}

export interface DeliverableCopy {
  mode: DeliverableMode
  /** Container count; null when the vessel count is unknown (bulk) or there is no package (loose). */
  count: number | null
  /** "Paper Food Cups with Lid (8 oz)", "Bulk Tray(s)", or the loose item's own phrase. */
  noun: string
  /** Ready to print: "4 Paper Food Cups with Lid (8 oz)" / "Bulk Tray(s)" / "16.4 gal whole full fat milk, in jugs". */
  headline: string
  /** The headline split for styling: `lead` is the number-ish part, `rest` the words. */
  headlineLead: string | null
  headlineRest: string
  /** "With (each container):" | "With:" | null when there is nothing to list. */
  contentsLabel: string | null
  /** What to make and how much. Never empty for a row with any component. */
  contents: ContentLine[]
  /** How to pack: the chip under the food line. */
  pack: { label: string; icon: PackIcon }
  /** Containers only: what goes in each one, without the trailing "each": "4 pancakes". Null otherwise. */
  eachLine: string | null
  /** The packaging SKU as the wire names it ("Bulk Tray"); null when loose. */
  packagingDisplay: string | null
}

const WHOLE_TOLERANCE = 0.02

/** Fractions of a PIECE a kitchen cuts: half a banana, a quarter muffin. */
const PIECE_FRACTIONS: ReadonlyArray<readonly [number, string, string]> = [
  [0.5, 'half', 'halves'],
  [0.25, 'quarter', 'quarters'],
  [1 / 3, 'third', 'thirds'],
]

/** Units that name a PART of a food rather than the food: "2 slices of french toast". */
const PORTION_UNITS: ReadonlySet<string> = new Set([
  'slice',
  'slices',
  'piece',
  'pieces',
  'wedge',
  'wedges',
  'scoop',
  'scoops',
  'disher',
  'half',
  'halves',
  'quarter',
  'quarters',
  'square',
  'squares',
  'stick',
  'sticks',
])

const LIQUID_UNITS: ReadonlySet<string> = new Set([
  'fl oz',
  'floz',
  'fl. oz',
  'fl.oz',
  'fluid ounce',
  'fluid ounces',
  'ml',
  'l',
])
const LIQUID_NAME = /\b(milk|juice|water|lemonade|gatorade|dressing|vinaigrette|sauce|syrup|smoothie|tea|cocoa|soup)\b/i

/** Within a hair of a whole number: 0.99 and 1.01 are 1. */
export function isWhole(n: number): boolean {
  return Math.abs(n - Math.round(n)) < WHOLE_TOLERANCE
}

function pieceFraction(q: number): { singular: string; plural: string } | null {
  for (const [value, singular, plural] of PIECE_FRACTIONS) {
    if (Math.abs(q - value) < WHOLE_TOLERANCE) {
      return { singular, plural }
    }
  }
  return null
}

/** A whole count with thousands separators: 2100 → "2,100". */
export function formatWholeCount(n: number): string {
  return Math.round(n).toLocaleString('en-US')
}

/** "1 serving", "387 servings", "2,100 servings": what the recipe pickers and the panel counts say. */
export function servingsLabel(n: number): string {
  return n === 1 ? '1 serving' : `${formatWholeCount(n)} servings`
}

function isPortionUnit(unit: string): boolean {
  const u = unit.trim().toLowerCase()
  return PORTION_UNITS.has(u) || /scoop|disher/.test(u)
}

/** A liquid is delivered by the fluid ounce, quart and gallon, never the cup. */
export function isLiquid(c: Pick<Component, 'name' | 'productType' | 'portion'>): boolean {
  const unit = c.portion?.unit.trim().toLowerCase() ?? ''
  return LIQUID_UNITS.has(unit) || c.productType === 'beverage' || LIQUID_NAME.test(c.name)
}

function kitchenNumber(value: number): string {
  const fraction = value - Math.floor(value)
  const snaps =
    fraction < WHOLE_TOLERANCE ||
    fraction > 1 - WHOLE_TOLERANCE ||
    PIECE_FRACTIONS.some(([f]) => Math.abs(fraction - f) < WHOLE_TOLERANCE) ||
    Math.abs(fraction - 2 / 3) < WHOLE_TOLERANCE ||
    Math.abs(fraction - 0.75) < WHOLE_TOLERANCE
  return snaps
    ? formatFractionalValue(value)
    : (Math.round(value * 10) / 10).toLocaleString('en-US', { maximumFractionDigits: 1 })
}

/**
 * A converted TOTAL, one unit, kitchen precision: "11.1 gal", "8 1/3 cups",
 * "11.1 lb", "30 fl oz". Fractions when the amount lands on one a cook can
 * measure, else one decimal.
 *
 *   solids   cups until a gallon, then gallons ("25 cups" is how the pass
 *            talks about corn; "1.6 gal" once it is a tray's worth)
 *   liquids  fl oz until a quart, quarts until a gallon, then gallons — nobody
 *            pours a jug by the cup
 *   weight   ounces until a pound, then pounds (the shared imperial ladder)
 *
 * COUNT units pass through untouched.
 */
export function formatTotalMeasure(total: number, unit: string, opts: { liquid?: boolean } = {}): string {
  if (measureDimension(unit) === 'VOLUME') {
    const cups = volumeToCups(total, unit)
    if (cups !== null && Number.isFinite(cups)) {
      if (cups >= 16) {
        return `${kitchenNumber(cups / 16)} gal`
      }
      if (opts.liquid) {
        return cups >= 4 ? `${kitchenNumber(cups / 4)} qt` : `${kitchenNumber(cups * 8)} fl oz`
      }
      return `${kitchenNumber(cups)} ${cups > 1 ? 'cups' : 'cup'}`
    }
  }
  const shown = toDisplaySystem(total, unit, 'imperial')
  return `${kitchenNumber(shown.value)} ${shown.unit}`
}

/**
 * Does the food name already carry this unit noun ("Wedges" in "seasoned potato
 * wedges", "Stick" in "breadsticks")? The noun has to END a word, plural or
 * not: "slice" inside "sliced" is a participle, not the noun ("Bread Sliced"
 * read "5 bread sliceds" while this was a substring test).
 */
function foodCarriesNoun(food: string, unitNoun: string): boolean {
  const stem = unitNoun.replace(/(es|s)$/, '')
  if (stem.length < 3) {
    return false
  }
  const escaped = stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`${escaped}(?:e?s)?\\b`, 'i').test(food)
}

/** The participle a name uses for the part it is served by: "Bread Sliced" served by the Slice. */
const PART_PARTICIPLES: Readonly<Record<string, string>> = {
  slice: 'sliced',
  slices: 'sliced',
  half: 'halved',
  halves: 'halved',
  quarter: 'quartered',
  quarters: 'quartered',
}

/**
 * The food without the participle its part unit already says: "whole grain
 * rich bread sliced" served by the slice → "whole grain rich bread", so the
 * line reads "5 slices of whole grain rich bread", not "slices of … sliced".
 */
function withoutPartParticiple(food: string, unitNoun: string): string {
  const participle = PART_PARTICIPLES[unitNoun]
  if (!participle) {
    return food
  }
  const words = food.split(' ')
  const kept = words.filter((w) => w.toLowerCase() !== participle)
  return kept.length > 0 && kept.length < words.length ? kept.join(' ') : food
}

/** The words after a count: "packets of ketchup" / "fresh bananas" / "mozzarella string cheese". */
function countLabel(n: number, k: KitchenName): string {
  if (k.vessel) {
    return `${n === 1 ? k.vessel : pluralizeWord(k.vessel)} of ${k.food}`
  }
  return n === 1 ? k.food : pluralizeFood(k.food)
}

/** The Cards amount cell for a count: "46 packets" / "355". */
function countAmount(n: number, k: KitchenName): string {
  const nText = formatWholeCount(n)
  return k.vessel ? `${nText} ${n === 1 ? k.vessel : pluralizeWord(k.vessel)}` : nText
}

/** The Cards name cell for a count: the food, plural when several and countable. */
function countName(n: number, k: KitchenName): string {
  return sentenceCase(k.vessel || n === 1 ? k.food : pluralizeFood(k.food))
}

interface LineExtras {
  total?: ContentTokens
  measured?: true
  imageUrl?: string | null
  volumeCups?: number | null
}

function line(
  kind: ContentLine['kind'],
  qty: string,
  label: string,
  food: string,
  cell: { amount: string; name: string },
  extras: LineExtras = {},
): ContentLine {
  return {
    kind,
    qty,
    label,
    food,
    phrase: qty.length > 0 ? `${qty} ${label}` : label,
    amount: cell.amount,
    name: cell.name,
    ...(extras.total ? { total: extras.total } : {}),
    ...(extras.measured ? { measured: true as const } : {}),
    ...(extras.imageUrl ? { imageUrl: extras.imageUrl } : {}),
    ...(extras.volumeCups !== undefined && extras.volumeCups !== null ? { volumeCups: extras.volumeCups } : {}),
  }
}

/** A counted item as tokens: "46" + "packets of ketchup", food "ketchup". */
function countTokens(n: number, k: KitchenName): ContentTokens & { food: string } {
  const food = k.vessel || n === 1 ? k.food : pluralizeFood(k.food)
  return { qty: formatWholeCount(n), label: countLabel(n, k), food }
}

/** The whole-order total of a counted item: the token pair without the food. */
function countTotal(n: number, k: KitchenName): ContentTokens {
  const t = countTokens(n, k)
  return { qty: t.qty, label: t.label }
}

function describeFood(c: Component, servings: number, mode: DeliverableMode): ContentLine {
  const k = kitchenName(c.name)
  const portion = c.portion
  const cell = { amount: '', name: sentenceCase(k.food) }
  const img = { imageUrl: c.imageUrl ?? null }

  if (!portion) {
    if (mode === 'containers') {
      return line('food', '', k.food, k.food, cell, img)
    }
    // No recipe: a countable head ("cheese pizza slice", "yogurt parfait") or a
    // named vessel ("bottled water") can still be counted; a bare mass noun can
    // only be servings.
    if (!k.uncountable || k.vessel) {
      const t = countTokens(servings, k)
      return line(
        'food',
        t.qty,
        t.label,
        t.food,
        { amount: countAmount(servings, k), name: countName(servings, k) },
        img,
      )
    }
    const n = formatWholeCount(servings)
    return line('food', n, `servings of ${k.food}`, k.food, { amount: `${n} servings`, name: cell.name }, img)
  }

  const { quantity: q, unit } = portion
  const dimension = measureDimension(unit)
  const liquid = isLiquid(c)

  if (dimension !== 'COUNT') {
    // A measured food. Standard units read lower-case whatever the author typed.
    const measureUnit = unit.trim().toLowerCase()
    if (mode === 'containers') {
      const per = formatMeasureText(q, measureUnit, { system: 'as-authored', use: 'COOKING' })
      const total = formatTotalMeasure(q * servings, measureUnit, { liquid })
      return line(
        'food',
        per,
        k.food,
        k.food,
        { amount: per, name: cell.name },
        {
          total: { qty: total, label: k.food },
          measured: true,
          ...img,
        },
      )
    }
    const total = formatTotalMeasure(q * servings, measureUnit, { liquid })
    const volumeCups = dimension === 'VOLUME' ? volumeToCups(q * servings, measureUnit) : null
    return line(
      'food',
      total,
      k.food,
      k.food,
      { amount: total, name: cell.name },
      { measured: true, volumeCups, ...img },
    )
  }

  // A counted food. The unit is a tally ("each"), a part ("Slice"), or the
  // thing itself ("Banana", "Sandwich") — the kitchen name carries the noun.
  const unitNoun = unit.trim().toLowerCase()
  const portionUnit = !isGenericCountUnit(unit) && isPortionUnit(unit) && !foodCarriesNoun(k.food, unitNoun)
  // The food a part unit is "of": the name minus a participle the unit restates.
  const partFood = portionUnit ? withoutPartParticiple(k.food, unitNoun) : k.food

  if (mode === 'containers') {
    const qText = formatFractionalValue(q)
    const totalCount = isWhole(q) ? Math.round(q) * servings : null
    if (portionUnit) {
      const noun = q > 1 ? pluralizeNoun(unitNoun) : unitNoun
      const total =
        totalCount !== null
          ? { qty: formatWholeCount(totalCount), label: `${pluralizeNoun(unitNoun)} of ${partFood}` }
          : undefined
      return line(
        'food',
        qText,
        `${noun} of ${partFood}`,
        partFood,
        { amount: `${qText} ${noun}`, name: sentenceCase(partFood) },
        {
          total,
          ...img,
        },
      )
    }
    if (isWhole(q)) {
      const n = Math.round(q)
      const t = countTokens(n, k)
      return line(
        'food',
        t.qty,
        t.label,
        t.food,
        { amount: countAmount(n, k), name: countName(n, k) },
        {
          total: totalCount !== null ? countTotal(totalCount, k) : undefined,
          ...img,
        },
      )
    }
    // A fraction of a piece per container: "1/2 fresh banana"; the order total
    // is "24 fresh banana halves".
    const fraction = pieceFraction(q)
    const total = fraction
      ? { qty: formatWholeCount(servings), label: `${k.food} ${servings === 1 ? fraction.singular : fraction.plural}` }
      : undefined
    return line('food', qText, k.food, k.food, { amount: qText, name: cell.name }, { total, ...img })
  }

  if (isWhole(q)) {
    const total = Math.round(q) * servings
    const n = formatWholeCount(total)
    if (portionUnit) {
      const noun = total === 1 ? unitNoun : pluralizeNoun(unitNoun)
      return line(
        'food',
        n,
        `${noun} of ${partFood}`,
        partFood,
        { amount: `${n} ${noun}`, name: sentenceCase(partFood) },
        img,
      )
    }
    const t = countTokens(total, k)
    return line('food', t.qty, t.label, t.food, { amount: countAmount(total, k), name: countName(total, k) }, img)
  }

  const fraction = pieceFraction(q)
  const n = formatWholeCount(servings)
  if (fraction) {
    // "355 fresh banana halves": the cut piece named after the thing it is cut from.
    const word = servings === 1 ? fraction.singular : fraction.plural
    return line('food', n, `${k.food} ${word}`, k.food, { amount: `${n} ${word}`, name: cell.name }, img)
  }

  // An odd fraction of a piece (1 1/2 tacos): state the multiplication.
  const qText = formatFractionalValue(q)
  return line('food', `${n}x ${qText}`, k.food, k.food, { amount: `${n}x ${qText}`, name: cell.name }, img)
}

function describeCondiment(c: Component, servings: number, mode: DeliverableMode): ContentLine {
  const k = kitchenName(c.name)
  // `condimentQty` is packets per meal, derived from the leaf's planned quantity
  // over the package count (floors at 1) — more trustworthy than a packet's
  // authored portion ("1 serving (30 ml)").
  const per = Math.max(1, Math.round(c.condimentQty ?? 1))
  const img = { imageUrl: c.imageUrl ?? null }
  if (mode === 'containers') {
    const t = countTokens(per, k)
    return line(
      'condiment',
      t.qty,
      t.label,
      t.food,
      { amount: countAmount(per, k), name: countName(per, k) },
      {
        total: countTotal(per * servings, k),
        ...img,
      },
    )
  }
  const total = per * servings
  const t = countTokens(total, k)
  return line('condiment', t.qty, t.label, t.food, { amount: countAmount(total, k), name: countName(total, k) }, img)
}

function describeSupply(s: SupplyLine, servings: number, mode: DeliverableMode): ContentLine {
  const name = (n: number) => pluralizePackagingName(s.name, n).toLowerCase()
  // Supply quantities arrive package-multiplied; per container they divide back.
  if (mode === 'containers') {
    const per = servings > 0 ? Math.max(1, Math.round(s.qty / servings)) : s.qty
    return line(
      'supply',
      String(per),
      name(per),
      name(per),
      { amount: String(per), name: sentenceCase(name(per)) },
      {
        total: { qty: formatWholeCount(s.qty), label: name(s.qty) },
      },
    )
  }
  const total = formatWholeCount(s.qty)
  return line('supply', total, name(s.qty), name(s.qty), { amount: total, name: sentenceCase(name(s.qty)) })
}

/** Which of the three grammars a row speaks. Exported for the layouts' placeholder art. */
export function deliverableMode(d: Pick<Deliverable, 'packaging' | 'bulk' | 'packagingSkuKind'>): DeliverableMode {
  if (!d.packaging) {
    return 'loose'
  }
  if (d.bulk || (d.packagingSkuKind ?? '').toUpperCase() === 'BULK') {
    return 'bulk'
  }
  return 'containers'
}

/** The pack chip's picture, from the packaging SKU name or the vessel word in the item's name. */
export function packIcon(packaging: string | null | undefined, vessel: string | null | undefined): PackIcon {
  const text = `${packaging ?? ''} ${vessel ?? ''}`.toLowerCase()
  if (text.trim().length === 0) {
    return 'none'
  }
  if (/\btrays?\b|\bpans?\b/.test(text)) {
    return 'tray'
  }
  if (/\bbox(es)?\b/.test(text)) {
    return 'box'
  }
  if (/\bcups?\b/.test(text)) {
    return 'cup'
  }
  if (/\bbags?\b|\bwraps?\b/.test(text)) {
    return 'bag'
  }
  if (/\bjugs?\b|\bcartons?\b|\bbottles?\b|\bpouch(es)?\b/.test(text)) {
    return 'jug'
  }
  if (/\bplates?\b/.test(text)) {
    return 'plate'
  }
  return 'container'
}

/** Participles a catalog head can END with ("Bread Sliced"): a title reads them first. */
const TRAILING_PARTICIPLES: ReadonlySet<string> = new Set([
  'sliced',
  'diced',
  'cubed',
  'chopped',
  'shredded',
  'grated',
  'halved',
  'quartered',
  'peeled',
  'seasoned',
  'steamed',
  'roasted',
  'mashed',
  'mixed',
])

/**
 * A food's kitchen words for a title, as one of it is named: "beef soft
 * taco", "fresh banana", "cubed fresh melon". A head that ends in a
 * participle reads it first and keeps its noun as it is: "whole grain rich
 * bread sliced" → "sliced whole grain rich bread".
 */
function titleWords(food: string): string {
  const parenAt = food.indexOf(' (')
  const core = parenAt === -1 ? food : food.slice(0, parenAt)
  const tail = parenAt === -1 ? '' : food.slice(parenAt)
  const words = core.split(' ')
  const last = words[words.length - 1]!
  if (words.length > 1 && TRAILING_PARTICIPLES.has(last.toLowerCase())) {
    return `${[last, ...words.slice(0, -1)].join(' ')}${tail}`
  }
  return food
}

/**
 * The deliverable's own name in kitchen words, each food as one of it is
 * named: "halal chicken tenders • seasoned potato wedges", "caesar salad with
 * halal grilled chicken". The row title is the bundle auto-title over the
 * catalog names (" & " within a kind, " • " between kinds), so each catalog
 * name in it is swapped for its kitchen words in place and the separators
 * stay the title's own. The field-trip prefix and the serving-size suffix are
 * flags of the row, not part of the name.
 */
function deliverableKitchenTitle(d: Pick<Deliverable, 'name' | 'components' | 'isFieldTrip' | 'servingSize'>): string {
  let title = d.name
  if (d.isFieldTrip) {
    title = title.replace(/^Bagged [^:]+:\s*/, '')
  }
  if (d.servingSize && title.endsWith(` — ${d.servingSize}`)) {
    title = title.slice(0, -` — ${d.servingSize}`.length)
  }
  const foods = d.components.filter((c) => !c.isCondiment)
  // An all-condiments package keeps its condiments in the title (the shared builder's rule).
  const named = foods.length > 0 ? foods : d.components
  const longestFirst = [...named].sort((a, b) => b.name.length - a.name.length)
  for (const c of longestFirst) {
    title = title.split(c.name).join(titleWords(kitchenName(c.name).food))
  }
  return title
}

/**
 * What leads a unitized card and the side panel's Deliver row: the containers
 * as a multiplier and the deliverable's name, "3x" + "Creamy Cheddar Mac
 * (White Penne) • Steamed Seasonal Veggie", "2x" + "Caesar Salad with Halal
 * Grilled Chicken". Abner, Sep 25: "If it's unitized, it's X (meals) not
 * Servings." The x counts meals, so the name is the name of ONE of them, in
 * kitchen words and never pluralized for the count; what goes in each
 * container ("4 pancakes") is listed under the package.
 */
export function unitizedLead(
  d: Pick<Deliverable, 'name' | 'components' | 'isFieldTrip' | 'servingSize'>,
  count: number,
): ContentTokens {
  return { qty: `${formatWholeCount(count)}x`, label: titleCaseFood(deliverableKitchenTitle(d)) }
}

/**
 * The Cards footer, the last thing on the card: how the contents ship, with
 * no count — the kitchen decides how many pans (Abner: "don't prescribe the
 * number of trays"). "Bulk Trays", "Paper Food Cups with Lid
 * (8 oz)"; a loose item names its vessel, "Jugs", or has "No package". No
 * "On"/"In" before it (Abner, Sep 23): the band reads as a label.
 */
export function packagingLine(copy: Pick<DeliverableCopy, 'packagingDisplay' | 'pack'>): string {
  return copy.packagingDisplay ? pluralizePackagingName(copy.packagingDisplay, 2) : copy.pack.label
}

/**
 * A quantity token split into the numeral and its unit, so a layout can weight
 * them apart — the digits large, the unit light, the way a kitchen display
 * does it: "11.1 gal" → { "11.1", "gal" }, "8 1/3 cups" → { "8 1/3", "cups" },
 * "30 fl oz" → { "30", "fl oz" }, "355" → { "355", "" }, "1/2 cup" → { "1/2",
 * "cup" }. A mixed count like "355x 1 1/2" keeps "355" as the numeral and the
 * rest as the unit, while a multiplier on its own ("3x", `unitizedLead`) is one
 * token: its x is not a unit to set apart. The bare fraction is tried first:
 * the whole-number branch would take the "1" of "1/2" and leave "/2 cup" as
 * the unit.
 */
export function splitQuantity(qty: string): { numeral: string; unit: string } {
  const trimmed = qty.trim()
  if (/^\d[\d,]*x$/.test(trimmed)) {
    return { numeral: trimmed, unit: '' }
  }
  const match = /^(\d+\/\d+|\d[\d,]*(?:\.\d+)?(?:\s+\d+\/\d+)?)\s*(.*)$/.exec(trimmed)
  if (!match) {
    return { numeral: trimmed, unit: '' }
  }
  return { numeral: match[1]!, unit: match[2]!.trim() }
}

/** What one content line was made from: a food, a condiment, or a supply. */
export type ContentSource =
  | { kind: 'food'; component: Component }
  | { kind: 'condiment'; component: Component }
  | { kind: 'supply'; supply: SupplyLine }

/**
 * The things a row lists, in the order `describeDeliverable` lists them:
 * foods, then condiments, then supplies. `contents[i]` is always made from
 * `contentSources(d)[i]`, so a surface that needs the numbers behind a line
 * (the detail panel's totals) can pair them by index.
 */
export function contentSources(
  d: Pick<Deliverable, 'id' | 'name' | 'productType' | 'components' | 'supplies'>,
): ContentSource[] {
  const sources: ContentSource[] = [
    ...d.components.filter((c) => !c.isCondiment).map((component) => ({ kind: 'food' as const, component })),
    ...d.components.filter((c) => c.isCondiment).map((component) => ({ kind: 'condiment' as const, component })),
    // Held with the chef supplies UI (`CHEF_SERVICE_MATERIALS_VISIBLE`): a row never lists its service materials.
    ...(CHEF_SERVICE_MATERIALS_VISIBLE ? (d.supplies ?? []) : []).map((supply) => ({
      kind: 'supply' as const,
      supply,
    })),
  ]
  if (sources.length === 0) {
    // A row with no component lines (legacy or aggregate data): its own title
    // stands in as the one thing to make, counted by the servings.
    sources.push({ kind: 'food', component: { id: d.id, name: d.name, productType: d.productType, diets: [] } })
  }
  return sources
}

function describeSource(source: ContentSource, servings: number, mode: DeliverableMode): ContentLine {
  switch (source.kind) {
    case 'food':
      return describeFood(source.component, servings, mode)
    case 'condiment':
      return describeCondiment(source.component, servings, mode)
    case 'supply':
      return describeSupply(source.supply, servings, mode)
  }
}

export function describeDeliverable(d: Deliverable): DeliverableCopy {
  const mode = deliverableMode(d)
  const servings = Math.max(0, Math.round(d.qty))
  // The SKU's own name, from the wire: no display rename on this port (the
  // branch's pan catalog, which read "Bulk Tray" as "Standard Catering Pan",
  // stays off main).
  const packagingDisplay = d.packaging ?? null
  const foods = d.components.filter((c) => !c.isCondiment)
  const contents: ContentLine[] = contentSources(d).map((source) => describeSource(source, servings, mode))

  if (mode === 'containers') {
    const noun = pluralizePackagingName(packagingDisplay!, servings)
    const lead = formatWholeCount(servings)
    const headline = `${lead} ${noun}`
    return {
      mode,
      count: servings,
      noun,
      headline,
      headlineLead: lead,
      headlineRest: noun,
      contentsLabel: contents.length > 0 ? 'With (each container):' : null,
      contents,
      pack: { label: headline, icon: packIcon(packagingDisplay, null) },
      eachLine: contents.length > 0 ? contents.map((l) => l.phrase).join(', ') : null,
      packagingDisplay,
    }
  }

  if (mode === 'bulk') {
    const noun = optionalPlural(packagingDisplay!)
    return {
      mode,
      count: null,
      noun,
      headline: noun,
      headlineLead: null,
      headlineRest: noun,
      contentsLabel: contents.length > 0 ? 'With:' : null,
      contents,
      pack: { label: noun, icon: packIcon(packagingDisplay, null) },
      eachLine: null,
      packagingDisplay,
    }
  }

  // Loose: no package line. The vessel word in the item's own name ("Jug",
  // "Carton") is the only packing fact there is.
  const vessels = foods.map((c) => kitchenName(c.name).vessel).filter((v): v is string => v !== null)
  const vessel = vessels[0] ?? null
  const pack = vessel
    ? { label: sentenceCase(pluralizeWord(vessel)), icon: packIcon(null, vessel) }
    : { label: 'No package', icon: 'none' as const }
  if (contents.length === 1) {
    const only = contents[0]!
    // A measured loose item names its vessel in the headline ("…, in jugs");
    // a counted one already counts it ("270 cartons of …").
    const countsVessel = vessel !== null && only.label.startsWith(`${pluralizeWord(vessel)} of `)
    const tail = vessel !== null && !countsVessel && only.qty.length > 0 ? `, in ${pluralizeWord(vessel)}` : ''
    return {
      mode,
      count: null,
      noun: only.phrase,
      headline: `${only.phrase}${tail}`,
      headlineLead: only.qty.length > 0 ? only.qty : null,
      headlineRest: `${only.label}${tail}`,
      contentsLabel: null,
      contents,
      pack,
      eachLine: null,
      packagingDisplay,
    }
  }
  const title = sentenceCase(kitchenName(d.name).food)
  return {
    mode,
    count: null,
    noun: title,
    headline: title,
    headlineLead: null,
    headlineRest: title,
    contentsLabel: contents.length > 0 ? 'With:' : null,
    contents,
    pack,
    eachLine: null,
    packagingDisplay,
  }
}
